import { convertAgePercentageToNumbers, convertObjectValuesToNumbers, toNumber } from '../helpers'

import {
  AGE_PERCENTAGE,
  AVERAGE_ANNUAL_COST,
  AVERAGE_DECREASED_ANXIETY_RATING_AFTER,
  AVERAGE_DECREASED_ANXIETY_RATING_BEFORE,
  AVERAGE_HAPPINESS_RATING_AFTER,
  AVERAGE_HAPPINESS_RATING_BEFORE,
  AVERAGE_LIFE_SATISFACTION_RATING_AFTER,
  AVERAGE_LIFE_SATISFACTION_RATING_BEFORE,
  AVERAGE_PURPOSE_OF_LIFE_RATING_AFTER,
  AVERAGE_PURPOSE_OF_LIFE_RATING_BEFORE,
  DISABILITY_PERCENTAGE,
  ETHNICITY_PERCENTAGE,
  ethnicityFields,
  GENDER_PERCENTAGE,
  IMD_PERCENTAGE,
  imdFields,
  SPORTS_TYPE,
  STAKEHOLDER_COUNT,
  STAKEHOLDER_TYPE,
  STAKEHOLDERS_LIST
} from '../stakeholdersFields'
import { COUNTRY_ID } from '../fields'

// Normalize a single IMD value (convert strings/null/undefined to numbers)
const normalizeImdValue = value => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  return typeof numValue === 'number' && !isNaN(numValue) ? numValue : 0
}

// Normalize all IMD fields and calculate unknown field
const normalizeAndCalculateImdPercentage = rawImdPercentage => {
  // Normalize imd_1 through imd_10
  const normalizedFields = Object.keys(imdFields)
    .filter(key => key !== 'unknown')
    .reduce((acc, key) => {
      acc[imdFields[key]] = normalizeImdValue(rawImdPercentage[imdFields[key]])
      return acc
    }, {})

  // Calculate sum and unknown field
  const sum = Object.values(normalizedFields).reduce((acc, val) => acc + val, 0)
  const calculatedUnknown = Math.max(0, 100 - sum)

  return {
    ...normalizedFields,
    [imdFields.unknown]: calculatedUnknown
  }
}

// Format ethnicity percentage - always return all fields
const formatEthnicityPercentage = stakeholder => {
  const ethnicityPercentage = stakeholder[ETHNICITY_PERCENTAGE]

  // Always send all 8 ethnicity fields in the payload
  // Fields not shown in UI for a country will have value 0
  return {
    [ethnicityFields.white]: toNumber(ethnicityPercentage[ethnicityFields.white]),
    [ethnicityFields.asian]: toNumber(ethnicityPercentage[ethnicityFields.asian]),
    [ethnicityFields.black]: toNumber(ethnicityPercentage[ethnicityFields.black]),
    [ethnicityFields.mixed]: toNumber(ethnicityPercentage[ethnicityFields.mixed]),
    [ethnicityFields.unknown]: toNumber(ethnicityPercentage[ethnicityFields.unknown]),
    [ethnicityFields.local]: toNumber(ethnicityPercentage[ethnicityFields.local]),
    [ethnicityFields.non_local]: toNumber(ethnicityPercentage[ethnicityFields.non_local]),
    [ethnicityFields.international]: toNumber(ethnicityPercentage[ethnicityFields.international])
  }
}

// Transform a single stakeholder from form format to backend format
const transformStakeholderToBE = (stakeholder, country) => {
  return {
    [STAKEHOLDER_TYPE]: stakeholder[STAKEHOLDER_TYPE],
    [SPORTS_TYPE]: stakeholder[SPORTS_TYPE],
    [STAKEHOLDER_COUNT]: toNumber(stakeholder[STAKEHOLDER_COUNT]),
    [AVERAGE_ANNUAL_COST]: toNumber(stakeholder[AVERAGE_ANNUAL_COST]),
    [GENDER_PERCENTAGE]: convertObjectValuesToNumbers(stakeholder[GENDER_PERCENTAGE]),
    [ETHNICITY_PERCENTAGE]: formatEthnicityPercentage(stakeholder),
    [DISABILITY_PERCENTAGE]: convertObjectValuesToNumbers(stakeholder[DISABILITY_PERCENTAGE]),
    [AGE_PERCENTAGE]: convertAgePercentageToNumbers(stakeholder[AGE_PERCENTAGE]),
    // Hide IMD % fields when country_of_operation: SAU
    ...(country !== 'SAU' && {
      [IMD_PERCENTAGE]: normalizeAndCalculateImdPercentage(stakeholder[IMD_PERCENTAGE] || {})
    }),

    [AVERAGE_LIFE_SATISFACTION_RATING_BEFORE]: toNumber(stakeholder[AVERAGE_LIFE_SATISFACTION_RATING_BEFORE]),
    [AVERAGE_LIFE_SATISFACTION_RATING_AFTER]: toNumber(stakeholder[AVERAGE_LIFE_SATISFACTION_RATING_AFTER]),
    [AVERAGE_PURPOSE_OF_LIFE_RATING_BEFORE]: toNumber(stakeholder[AVERAGE_PURPOSE_OF_LIFE_RATING_BEFORE]),
    [AVERAGE_PURPOSE_OF_LIFE_RATING_AFTER]: toNumber(stakeholder[AVERAGE_PURPOSE_OF_LIFE_RATING_AFTER]),
    [AVERAGE_HAPPINESS_RATING_BEFORE]: toNumber(stakeholder[AVERAGE_HAPPINESS_RATING_BEFORE]),
    [AVERAGE_HAPPINESS_RATING_AFTER]: toNumber(stakeholder[AVERAGE_HAPPINESS_RATING_AFTER]),
    [AVERAGE_DECREASED_ANXIETY_RATING_BEFORE]: toNumber(stakeholder[AVERAGE_DECREASED_ANXIETY_RATING_BEFORE]),
    [AVERAGE_DECREASED_ANXIETY_RATING_AFTER]: toNumber(stakeholder[AVERAGE_DECREASED_ANXIETY_RATING_AFTER])
  }
}

// Transform stakeholders list from form format to backend format
//
// - age_percentage: each age group contains total + all activity level percentages
// - imd_percentage: all fields normalized to numbers, unknown field auto-calculated
export const formatStakeholdersListToBE = values => {
  const stakeholdersList = values[STAKEHOLDERS_LIST]
  const country = values[COUNTRY_ID]

  return stakeholdersList.map(stakeholder => transformStakeholderToBE(stakeholder, country))
}

// Transform a single stakeholder from backend format to form format
const transformStakeholderFromBE = stakeholder => {
  // Form format now matches backend format, so return as-is
  return stakeholder
}

// Transform stakeholders list from backend format to form format
//
// - age_percentage: each age group contains total + all activity level percentages
export const formatStakeholdersListFromBE = stakeholdersList => {
  if (!stakeholdersList || !Array.isArray(stakeholdersList)) {
    return []
  }

  return stakeholdersList.map(transformStakeholderFromBE)
}
